/**
 * THE NARRATIVE PLAN — content selection before wording (narration review
 * 2026-09-26, Option D). A plan records the governing fact, up to three
 * supporting facts, the domain lens's mandatory qualifications and the
 * follow-ups it allows. Implication and action stay null until an evidenced
 * finding backs them: a plan never asserts what the engine did not compute.
 *
 * Facts name binding placeholders by role; values, signs and statuses come
 * from the engine (a binding's typed `value`, a finding's typed status), never
 * from label text.
 */
import type { AskHelixNumericBindingDto } from '../../ask-helix';
import type { AskDeclaredExhibitBody } from '../contracts/declared-exhibit';
import type { AskNarrativeLens, AskPlanRelation, AskPlanState } from './narrative-lens';
import { periodLabel, snapshotDateLabel } from './period-label';

export const ASK_PLAN_MAX_SUPPORTING = 3;
export const ASK_PLAN_MAX_FOLLOW_UPS = 2;

export type AskPlanFact = Readonly<{
  relation: AskPlanRelation;
  /** The governed label the fact is about. */
  subject: string;
  /** Binding placeholders (or a view's figure refs) by role: part, whole, from, to, change. */
  figures: Readonly<Record<string, string>>;
  /** Governed labels by role (period labels, scope), never numbers. */
  labels: Readonly<Record<string, string>>;
  states: readonly AskPlanState[];
  /** The RuntimeV3Finding behind the fact, when one computed it. */
  findingId: string | null;
}>;

export type AskNarrativePlan = Readonly<{
  lensId: string | null;
  governing: AskPlanFact;
  supporting: readonly AskPlanFact[];
  /** Null until an evidenced implication finding exists (never inferred here). */
  implication: null;
  /** The lens's advisory action the governing fact allows, or null (never free text). */
  action: Readonly<{ id: string }> | null;
  qualificationIds: readonly string[];
  followUpIds: readonly string[];
}>;

/**
 * Orders supporting facts by the lens's priorities, keeps at most three, and
 * attaches every qualification a stated fact makes mandatory plus up to two
 * follow-ups for the governing relation.
 */
export function completeNarrativePlan(
  governing: AskPlanFact,
  supporting: readonly AskPlanFact[],
  lens: AskNarrativeLens | null
): AskNarrativePlan {
  const rank = (fact: AskPlanFact): number => {
    const at = lens?.priorities.indexOf(fact.relation) ?? -1;
    return at < 0 ? Number.MAX_SAFE_INTEGER : at;
  };
  const ordered = [...supporting]
    .sort((a, b) => rank(a) - rank(b))
    .slice(0, ASK_PLAN_MAX_SUPPORTING);
  const stated = new Set([governing, ...ordered].flatMap((fact) => fact.states));
  return {
    lensId: lens?.lensId ?? null,
    governing,
    supporting: ordered,
    implication: null,
    action: advisoryAction(governing, lens),
    qualificationIds: (lens?.qualifications ?? [])
      .filter((item) => item.requiredWhen.some((state) => stated.has(state)))
      .map((item) => item.id),
    followUpIds: (lens?.followUps ?? [])
      .filter((item) => item.relation === governing.relation)
      .filter(
        (item) =>
          !item.whenStates || item.whenStates.some((state) => governing.states.includes(state))
      )
      .slice(0, ASK_PLAN_MAX_FOLLOW_UPS)
      .map((item) => item.id),
  };
}

type BridgeExhibit = Extract<AskDeclaredExhibitBody, { kind: 'bridge' }>;

/**
 * A bridge's governing fact: the engine's change, signed by its bound value.
 * Periods come from the typed windows (`periodLabel`); the scope reads through
 * the publication-only display layer and is never dropped.
 */
export function bridgeChangeFact(
  exhibit: BridgeExhibit,
  bindings: readonly AskHelixNumericBindingDto[],
  displayLabel: (raw: string) => string = (raw) => raw
): AskPlanFact | null {
  const byPlaceholder = new Map(bindings.map((binding) => [binding.placeholder, binding]));
  const change = byPlaceholder.get(exhibit.change.figure);
  const from = byPlaceholder.get(exhibit.from.figure);
  const to = byPlaceholder.get(exhibit.to.figure);
  if (!change || !from || !to || typeof change.value !== 'number') return null;
  if (samePeriod(from, to))
    return gapFact(exhibit.change.figure, change.value, from, to, displayLabel);
  const direction: AskPlanState[] =
    change.value > 0 ? ['increase'] : change.value < 0 ? ['decrease'] : [];
  return {
    relation: 'change',
    subject: exhibit.subject,
    figures: { from: exhibit.from.figure, to: exhibit.to.figure, change: exhibit.change.figure },
    labels: {
      scope: displayLabel(change.scope.label),
      fromPeriod: periodLabel(from.timeframe),
      toPeriod: periodLabel(to.timeframe),
    },
    states: direction,
    findingId: null,
  };
}

/** Both sides read over the same typed window: the bridge compares them, not two periods. */
function samePeriod(from: AskHelixNumericBindingDto, to: AskHelixNumericBindingDto): boolean {
  const [a, b] = [from.timeframe, to.timeframe];
  return a.start != null && a.end != null && a.start === b.start && a.end === b.end;
}

/**
 * Two measure labels named by what differs (lead polish 07:25): leading words both share are
 * said once by neither ("Synergy planned value" / "Synergy realized value" → "Planned value" /
 * "realized value"). Only the subject's first letter is capitalized; nothing is lowercased, so a
 * proper noun is never damaged. No shared word, or nothing left after it → the whole labels.
 */
function differingWords(subject: string, reference: string): [string, string] {
  const [a, b] = [subject.split(' '), reference.split(' ')];
  let shared = 0;
  while (shared < a.length - 1 && shared < b.length - 1 && a[shared] === b[shared]) shared += 1;
  if (shared === 0) return [subject, reference];
  const rest = a.slice(shared).join(' ');
  return [rest.charAt(0).toUpperCase() + rest.slice(1), b.slice(shared).join(' ')];
}

/**
 * HOTFIX 2026-09-27 (lead 07:14): a same-period bridge is a gap between its two sides, named by
 * what actually differs — their measures ("Synergy planned value … Synergy realized value") or,
 * for one measure, their scopes (two companies). Signed by the engine's bound difference
 * (to − from); never "rose/fell", never "2026 vs 2026". Nothing distinguishes the sides, or no
 * difference → no fact.
 */
function gapFact(
  gap: string,
  value: number,
  from: AskHelixNumericBindingDto,
  to: AskHelixNumericBindingDto,
  displayLabel: (raw: string) => string
): AskPlanFact | null {
  const [fromScope, toScope] = [displayLabel(from.scope.label), displayLabel(to.scope.label)];
  const measures = to.displayLabel !== from.displayLabel;
  if (value === 0 || !to.displayLabel || !from.displayLabel) return null;
  if (!measures && fromScope === toScope) return null;
  const period = periodLabel(to.timeframe);
  const [subject, reference] = measures
    ? differingWords(to.displayLabel, from.displayLabel)
    : [to.displayLabel, fromScope];
  const labels: Record<string, string> = measures
    ? { reference, scope: toScope, period, sides: 'measures' }
    : { reference, scope: toScope, period, sides: 'scopes' };
  return {
    relation: 'gap_to_reference',
    subject,
    figures: { gap },
    labels,
    states: value < 0 ? ['below_reference'] : [],
    findingId: null,
  };
}

/**
 * The engine's rank receipt for one output (`RuntimeV3ResultEnvelope.rank`), handed to publication
 * by the server. Direction is the only thing narration reads from it: counts are not bound
 * figures, so no sentence prints them.
 */
export type AskNarrationRank = Readonly<{ outputId: string; direction: 'asc' | 'desc' }>;

type RankedBars = Extract<AskDeclaredExhibitBody, { kind: 'ranked_bars' }>;

/**
 * A ranking's governing fact (narration slice 1): its leader and the next member, each with its
 * own bound figure. The declaration draws bars largest first, so a top ranking leads with the
 * first bar and a bottom ranking with the last; without the engine's receipt the direction is
 * unknown and there is no fact.
 */
export function rankLeaderFact(
  exhibit: RankedBars,
  bindings: readonly AskHelixNumericBindingDto[],
  context: Readonly<{
    measure: (outputId: string) => string | null;
    ranks: readonly AskNarrationRank[];
  }>,
  displayLabel: (raw: string) => string = (raw) => raw
): AskPlanFact | null {
  const order = [...exhibit.members];
  const leaderBinding = bindings.find((binding) => binding.placeholder === order[0]?.figure);
  const outputId = leaderBinding?.outputId;
  const rank = context.ranks.find((receipt) => receipt.outputId === outputId);
  const measure = outputId === undefined ? null : context.measure(outputId);
  if (!rank || !measure || order.length < 2) return null;
  if (rank.direction === 'asc') order.reverse();
  const [leader, next] = order;
  return {
    relation: 'largest',
    subject: measure,
    figures: { leader: leader.figure, next: next.figure },
    labels: {
      leader: displayLabel(leader.label),
      next: displayLabel(next.label),
      end: rank.direction === 'asc' ? 'bottom' : 'top',
    },
    states: [],
    findingId: null,
  };
}

/**
 * A single figure's governing fact (narration slice 1): the value itself, answer first, with its
 * human scope and period. A snapshot is dated from its typed as-of day ("as of 31 Aug 2026"); a
 * window reads through the shared period rule ("2024", "Q1 2025", or its governed label).
 */
export function kpiFigureFact(
  subject: string,
  binding: AskHelixNumericBindingDto,
  displayLabel: (raw: string) => string = (raw) => raw,
  measure: string | null = null
): AskPlanFact {
  const { timeframe } = binding;
  // A snapshot, or a window that starts and ends on one day (headed run 08:40), is a date.
  const oneDay =
    timeframe.kind === 'window' && timeframe.start && timeframe.start === timeframe.end;
  const asOf =
    timeframe.kind === 'window' && !oneDay
      ? null
      : snapshotDateLabel({ start: timeframe.asOf, end: timeframe.asOf });
  const period = timeframe.label ? periodLabel(timeframe) : '';
  const when: Record<string, string> = asOf !== null ? { asOf } : period ? { period } : {};
  // A grouped row's label is its MEMBER, not a measure (headed run 08:40: "Amendment
  // required … : −$1.8M"): state the typed attribute, and the figure under the output's measure.
  const attribute = coordinateAttribute(binding);
  const named = attribute !== null && measure !== null;
  return {
    relation: 'figure',
    subject: named ? measure : subject,
    figures: { value: binding.placeholder },
    labels: {
      scope: displayLabel(binding.scope.label),
      ...when,
      ...(named ? { attribute } : {}),
    },
    states: [],
    findingId: null,
  };
}

/** "Drift status: Amendment required" from the binding's typed coordinate names, or null. */
export function coordinateAttribute(binding: AskHelixNumericBindingDto): string | null {
  const listed = (binding as Readonly<{ coordinateLabels?: unknown }>).coordinateLabels;
  if (!Array.isArray(listed) || listed.length === 0) return null;
  const parts = (listed as readonly Readonly<Record<string, unknown>>[]).map((entry) =>
    typeof entry?.dimension === 'string' && typeof entry?.member === 'string' && entry.member
      ? `${entry.dimension}: ${entry.member}`
      : null
  );
  return parts.every((part) => part !== null) ? parts.join('; ') : null;
}

/**
 * "{part} of {whole} {subject} is at risk" — the value-capture headline's
 * governing fact. The caller supplies the engine's at-risk figure and the SAME
 * committed base the dashboard shows (lead ruling 12:53); the plan only names them.
 */
export function atRiskFact(
  input: Readonly<{
    subject: string;
    part: string;
    whole: string;
    asOf: string | null;
  }>
): AskPlanFact {
  return {
    relation: 'part_of_whole',
    subject: input.subject,
    figures: { part: input.part, whole: input.whole },
    labels: input.asOf ? { asOf: input.asOf } : {},
    states: ['at_risk'],
    findingId: null,
  };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Aug 31, 2026" from a typed ISO date; anything else is not a date label. */
function dateLabel(iso: string | null): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  return match ? `${MONTHS[Number(match[2]) - 1]} ${Number(match[3])}, ${match[1]}` : null;
}

/**
 * The value-capture stage headline (helix-f9, 2026-09-26): realized of the
 * committed base as of the actuals date, then validated by approved decisions.
 * An untracked stage (null figure) drops its clause; neither tracked → null.
 */
export function valueCaptureFact(
  input: Readonly<{
    subject: string;
    committed: string;
    realized: string | null;
    validated: string | null;
    realizedAsOf: string | null;
  }>
): AskPlanFact | null {
  const asOf = dateLabel(input.realizedAsOf);
  const realized = input.realized !== null && asOf !== null ? input.realized : null;
  if (realized === null && input.validated === null) return null;
  return {
    relation: 'part_of_whole',
    subject: input.subject,
    figures: {
      whole: input.committed,
      ...(realized ? { realized } : {}),
      ...(input.validated ? { validated: input.validated } : {}),
    },
    labels: asOf && realized ? { asOf } : {},
    states: [
      ...(realized ? ['realized' as const] : []),
      ...(input.validated ? ['validated' as const] : []),
    ],
    findingId: null,
  };
}

/** The first advisory action the lens allows for what the governing fact states. */
function advisoryAction(
  governing: AskPlanFact,
  lens: AskNarrativeLens | null
): Readonly<{ id: string }> | null {
  const allowed = (lens?.advisoryActions ?? []).find((action) =>
    action.requiredWhen.some((state) => governing.states.includes(state))
  );
  return allowed ? { id: allowed.id } : null;
}

/**
 * The value-capture dashboard headline (partner review, helix-f9 bindings): achieved
 * run-rate against the current plan and the announced commitment. A plan change
 * becomes a `plan_reduced` state only when its BOUND value is below zero (a $0 change
 * is no reduction; keyed on the value, never its text).
 */
export function captureProgressFact(
  input: Readonly<{
    subject: string;
    committed: string;
    plan: string;
    achieved: string;
    achievedAsOf: string | null;
    planChange: Readonly<{ figure: string; value: number }> | null;
  }>
): AskPlanFact {
  const reduced = input.planChange !== null && input.planChange.value < 0;
  const asOf = dateLabel(input.achievedAsOf);
  return {
    relation: 'progress',
    subject: input.subject,
    figures: {
      achieved: input.achieved,
      plan: input.plan,
      committed: input.committed,
      ...(reduced && input.planChange ? { planChange: input.planChange.figure } : {}),
    },
    labels: asOf ? { asOf } : {},
    states: ['run_rate', ...(reduced ? (['plan_reduced'] as const) : [])],
    findingId: null,
  };
}
